import { useCallback, useEffect, useState } from "react";
import classNames from "classnames";

import { BACKEND_CONFIG } from "../../config/consts";
import { ActivityLogConsumer } from "../../context/activity-log-context";
import FormatUtils from "../../utils/formatUtils";

import { QuoteSpinnerConsumer } from "../../context/quote-spinner-context";
import InfoIcon from "../../images/info.png"
import CarrierSquareIcon from "../lob-details/CarrierSquareIcon";
import QuoteDescriprionModal from "../QuoteDescriptionModal";
import BouncyBanner from "../BouncyBanner";

import styles from "./PolicyQuoteCard2.module.scss";

type Data = {
  PolicyQuoteId: string;
  Id: number;
  ClientId: string;
  CarrierId: number;
  Lob: string;
  Status: string;
  Carrier: string;
  FriendlyCarrierName: string;
  EffectiveDate: string;
  ExpirationDate: string;
  IsPolicy: boolean;
  IsExpired: boolean;
  Ready: boolean;
  Premium: number | null;
  CarrierMessages: {
    Message: string;
  }[] | null;
  Description: string | null;
  SupportsVersioning: boolean;
  IsViewDetailsButtonVisible: boolean;
  SurplusLines: boolean;
  Number: string | null;
  RefreshInMiliseconds: number;
};

function NewActionsCount({
  itemId
}: {
  itemId: string
}) {
  return (
    <ActivityLogConsumer>
      {activityLogContext => {
        const unreadNotificationsCount = activityLogContext.getQuoteUnreadNotificationsCount(itemId);

        if (unreadNotificationsCount === 0) {
          return null;
        }

        return (
          <div
            className="x-horizontal x-font-size-12px x-font-weight-700 x-align-middle-center"
            style={{ background: "#eb3637", width: 18, height: 18, borderRadius: 9999, color: "white" }}
          >
            {unreadNotificationsCount}
          </div>
        );
      }}
    </ActivityLogConsumer>
  );
}

function getStatusTextColor(status: string, isExpired: boolean = false) {
  return FormatUtils.getStatusStyle(status, isExpired, { v2: true }).fontColor;
}

function getStatusBackgroundColor(status: string, isExpired: boolean = false) {
  return FormatUtils.getStatusStyle(status, isExpired, { v2: true }).statusBg;
}

const surplusLinesBadge = (
  <div className="x-font-size-12px x-font-weight-600 x-font-heading" style={{
    border: "1px solid #979797",
    color: "#707070",
    padding: "1px 4px",
    borderRadius: 4
  }}>
    Surplus Lines
  </div>
)

function PolicyQuoteCard2({
  data,
  selectedPolicyQuoteId,
  hideLobDetails,
  viewCallback,
  onReload
}: {
  data: Data;
  selectedPolicyQuoteId: string;
  hideLobDetails: () => void;
  viewCallback: ({ policyQuoteId, isPolicy, supportsVersioning }: {
    policyQuoteId: string;
    isPolicy: boolean;
    supportsVersioning: boolean;
  }) => void;
  onReload: () => void;
}) {
  const { PolicyQuoteId, Id, Number, ClientId, CarrierId, Lob, Status, Carrier, FriendlyCarrierName, Premium } = data;
  const { Ready, IsPolicy, SupportsVersioning, IsViewDetailsButtonVisible } = data;
  const { Description, CarrierMessages, SurplusLines, EffectiveDate, ExpirationDate } = data;
  const { RefreshInMiliseconds } = data;

  const [quoteDescription, setQuoteDescription] = useState(Description ?? "");
  const [isEditDescriptionModalVisible, setIsQuoteDescriptionModalVisible] = useState(false);
  const [isCarrierMessagesExpanded, setIsCarrierMessagesExpanded] = useState(false);

  //

  const handleViewDetailsClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault();

    window.scrollTo(0, 0);

    if (PolicyQuoteId === selectedPolicyQuoteId) {
      hideLobDetails();

      return;
    }

    viewCallback({
      policyQuoteId: PolicyQuoteId,
      isPolicy: IsPolicy,
      supportsVersioning: SupportsVersioning
    });
  }, [IsPolicy, PolicyQuoteId, SupportsVersioning, hideLobDetails, selectedPolicyQuoteId, viewCallback]);

  const handleEditDescriptionClick = useCallback(() => {
    setIsQuoteDescriptionModalVisible(true);
  }, []);

  const handleEditDescriptionModalSave = useCallback((description: string) => {
    setQuoteDescription(description);
    setIsQuoteDescriptionModalVisible(false);
  }, []);

  const handleEditDescriptionModalClose = useCallback(() => {
    setIsQuoteDescriptionModalVisible(false);
  }, []);

  const handleToggleCarrierMessages = useCallback(() => {
    setIsCarrierMessagesExpanded(!isCarrierMessagesExpanded)
  }, [isCarrierMessagesExpanded]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    if (RefreshInMiliseconds > 0) {
      timeout = setTimeout(onReload, RefreshInMiliseconds);
    }

    return () => {
      clearTimeout(timeout);
    }
  }, [RefreshInMiliseconds, onReload]);

  //

  if (!Ready) {
    return null;
  }

  const viewDetailsButtonText = PolicyQuoteId === selectedPolicyQuoteId
    ? IsPolicy ? "Close Details" : "Close Quote Details"
    : IsPolicy ? "View Details" : "View Quote Details";

  const cardClassName = classNames(styles.Card, "x-vertical", {
    [styles.selected]: PolicyQuoteId === selectedPolicyQuoteId,
    [styles.expired]: Status === "Cancelled" || Status === "Expired"
  });

  return (
    <div
      data-testid="quote-card"
      data-carrier={Carrier}
      data-lob={Lob}
      data-bindable={Status === "Ready to Bind"}
      className={cardClassName}
    >
      <div style={{ padding: "14px" }} className="x-vertical x-spacing-12px">
        <div className="x-horizontal">
          <CarrierSquareIcon carrierId={CarrierId} className={styles.carrierLogo} />
          <div className="x-horizontal x-flex-1 x-spacing-4px" style={{ marginTop: 8 }}>
            <div className="x-vertical x-flex-1 x-spacing-4px">
              <div data-testid="quote-card-carrier-label" className="x-font-size-16px x-font-weight-700 x-font-heading">
                {FriendlyCarrierName ?? Carrier}
              </div>
              <div data-testid="quote-card-lob-label" className="x-font-size-12px x-font-weight-600 x-font-heading">
                {Lob}
              </div>
            </div>
            {Premium && (
              <div data-testid="quote-card-premium-label" className="x-font-size-16px x-font-weight-700 x-font-heading">
                {Premium.toLocaleString(undefined, {
                  currency: "USD",
                  style: "currency"
                })}
              </div>
            )}
          </div>
        </div>
        <div className="x-horizontal x-align-bottom-left">
          <div className="x-vertical x-flex-1 x-spacing-4px">
            {IsPolicy && (
              <>
                <div className="x-font-size-12px x-font-heading">
                  <span style={{ color: "#636176" }}>Policy ID:</span>{' '}
                  <span className="x-font-weight-600">{Id}</span>
                </div>
                <div className="x-font-size-12px x-font-heading">
                  <span style={{ color: "#636176" }}>Policy Number:</span>{' '}
                  <span className="x-font-weight-600">{Number ?? "—"}</span>
                </div>
                <div className="x-font-size-12px x-font-heading">
                  <span style={{ color: "#636176" }}>Expiration Date:</span>{' '}
                  <span className="x-font-weight-600">
                    {ExpirationDate ? new Date(ExpirationDate).toLocaleDateString() : "—"}
                  </span>
                </div>
              </>
            )}
            {!IsPolicy && (
              <>
                <div className="x-font-size-12px x-font-heading">
                  <span style={{ color: "#636176" }}>Quote ID:</span>{' '}
                  <span data-testid="quote-card-quote-id-label" className="x-font-weight-600">
                    {Id}
                  </span>
                </div>
                <div className="x-font-size-12px x-font-heading">
                  <span style={{ color: "#636176" }}>Effective Date:</span>{' '}
                  <span className="x-font-weight-600">
                    {EffectiveDate ? new Date(EffectiveDate).toLocaleDateString() : "—"}
                  </span>
                </div>
              </>
            )}
          </div>
          {SurplusLines && (
            surplusLinesBadge
          )}
        </div>
        <QuoteSpinnerConsumer>
          {(spinnerContext) => {
            const showBanner =
              PolicyQuoteId === spinnerContext.selectedQuoteId && spinnerContext.quotePreparing;
            return (
              showBanner && <BouncyBanner bannerText={spinnerContext.text} />
            );
          }}
        </QuoteSpinnerConsumer>
        {BACKEND_CONFIG.get("quoteVersioningVisible") && !IsPolicy && (
          <div className="x-vertical x-spacing-4px">
            <div className="x-horizontal x-align-middle-justify">
              <div className="x-link x-font-size-12px x-font-weight-600 x-font-heading" onClick={handleEditDescriptionClick}>
                {quoteDescription !== "" ? "Edit " : "Add "} Description
              </div>
            </div>
            {quoteDescription && (
              <div className="x-font-size-12px x-font-heading">
                {quoteDescription}
              </div>
            )}
          </div>
        )}
      </div>
      <hr className="x-divider" style={{ margin: "0 14px" }} />
      <div style={{ padding: "12px 14px" }} className="x-horizontal x-align-middle-justify">
        <div className="x-horizontal x-spacing-8px">
          <div data-testid="quote-card-status-label" className="x-font-size-12px x-font-weight-600 x-font-heading" style={{
            background: getStatusBackgroundColor(Status),
            color: getStatusTextColor(Status),
            padding: "2px 4px",
            borderRadius: 4
          }}>
            {Status}
          </div>
        </div>
        {IsViewDetailsButtonVisible && (
          <div className="x-horizontal x-align-middle-left x-spacing-8px">
            <NewActionsCount itemId={PolicyQuoteId} />
            <div
              data-testid="quote-card-view-details-button"
              className="x-link x-font-size-12px x-font-weight-600 x-font-heading"
              onClick={handleViewDetailsClick}
            >
              {viewDetailsButtonText}
            </div>
          </div>
        )}
      </div>
      {CarrierMessages && CarrierMessages.length !== 0 && (
        <div style={{ background: "#f3f3f3", padding: "12px 14px", cursor: "pointer" }} onClick={handleToggleCarrierMessages}>
          <div className="x-horizontal x-align-middle-justify">
            <div className="x-horizontal x-align-middle-left x-spacing-4px">
              <img src={InfoIcon} alt="" style={{ height: 22, margin: "-2px 0" }} />
              <div className="x-font-size-12px x-font-weight-600">
                Messages
              </div>
            </div>
            <div className="x-link x-font-size-12px x-font-weight-600">
              {isCarrierMessagesExpanded ? "Hide ▴" : "Show ▾"}
            </div>
          </div>
          {isCarrierMessagesExpanded && (
            <ul style={{ margin: 0, padding: 0, paddingLeft: 14, listStyle: "square" }}>
              {CarrierMessages.map((message: any, index: number) => (
                <li key={index} className="x-font-size-11px" style={{ padding: 0, marginTop: 10 }}>
                  {message.Message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <QuoteDescriprionModal
        isVisible={isEditDescriptionModalVisible}
        description={quoteDescription}
        quoteId={PolicyQuoteId}
        clientId={ClientId}
        updateDescription={handleEditDescriptionModalSave}
        close={handleEditDescriptionModalClose}
      />
    </div>
  );
}

export default PolicyQuoteCard2;
